import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { useContent } from "../lib/useContent";
import { useSeo, Reveal, LazyImg } from "../components/common";
import { PageHero } from "../components/PageHero";
import { SectionHead, AISearch, CTABand } from "../components/blocks";
import { ServiceTextCard } from "../components/cards";
import { CATEGORY_META, CATEGORY_NAMES, IMG } from "../data/site";

export default function Services() {
  const { t, lang } = useLang();
  useSeo("Cleaning Services | Sparko Netherlands", "Explore Sparko's full range of premium cleaning services: office, deep, floor, carpet, industrial and specialised cleaning across the Netherlands.");
  const { data: services } = useContent("services");
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(params.get("cat") || "all");
  useEffect(() => { setActive(params.get("cat") || "all"); }, [params]);

  // group by category key (preserves order), keeping the display name
  const groups = useMemo(() => {
    const g = {};
    services.forEach((s) => {
      if (!g[s.category]) g[s.category] = { key: s.category, name: s.category_name, items: [] };
      g[s.category].items.push(s);
    });
    return Object.values(g);
  }, [services]);

  const categories = groups.map((g) => [g.key, g.name]);
  const shown = active === "all" ? groups : groups.filter((g) => g.key === active);

  return (
    <div>
      <PageHero title={t.sections.servicesTitle} crumb={t.nav.services} bg={IMG.commercial}
        sub={t.sections.servicesSub} />

      <section className="mx-auto max-w-5xl px-5 md:px-8 -mt-10 relative z-20 pb-6">
        <AISearch />
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-8 py-14">
        <div className="flex flex-wrap gap-2 justify-center" data-testid="service-filters">
          <button onClick={() => setParams({})} data-testid="filter-all"
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${active === "all" ? "bg-navy text-white" : "bg-muted text-navy hover:bg-blue/10"}`}
            style={active !== "all" ? { background: "var(--muted)" } : {}}>{lang === "nl" ? "Alle" : lang === "ar" ? "الكل" : "All"}</button>
          {categories.map(([key, name]) => (
            <button key={key} onClick={() => setParams({ cat: key })} data-testid={`filter-${key}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${active === key ? "bg-navy text-white" : "bg-muted text-navy hover:bg-blue/10"}`}
              style={active !== key ? { background: "var(--muted)" } : {}}>{CATEGORY_NAMES[key]?.[lang] || name}</button>
          ))}
        </div>

        <div className="mt-16 space-y-20">
          {shown.map((g) => {
            const meta = CATEGORY_META[g.key] || {};
            const catImg = IMG[meta.img] || g.items[0]?.image;
            return (
              <div key={g.key} className="grid md:grid-cols-12 gap-8 lg:gap-12 items-start" data-testid={`category-${g.key}`}>
                <div className="md:col-span-4">
                  <Reveal>
                    <div className="md:sticky md:top-28">
                      <div className="relative overflow-hidden rounded-3xl aspect-[4/3] shadow-lg">
                        <LazyImg src={catImg} alt={CATEGORY_NAMES[g.key]?.[lang] || g.name} className="h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                      </div>
                      <SectionHead eyebrow={`${g.items.length} ${lang === "nl" ? "diensten" : lang === "ar" ? "خدمات" : "services"}`} title={CATEGORY_NAMES[g.key]?.[lang] || g.name} />
                    </div>
                  </Reveal>
                </div>
                <div className="md:col-span-8">
                  <div className="grid sm:grid-cols-2 gap-3">
                    {g.items.map((s, i) => <ServiceTextCard key={s.id} svc={s} index={i} />)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CTABand />
    </div>
  );
}
